/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useEffect, useRef } from 'react'
import { LineChart, Card, Title, Text } from '@tremor/react'
import { CircularProgress, IconButton } from '@material-ui/core'
import GetAppIcon from '@material-ui/icons/GetApp'

import moment from 'config/moment'
import { useApiCall } from 'hooks/useApi'

import { useLoadingTracker } from '../../hooks/useLoadingTracker'
import { CustomCheckbox } from '../../components/CustomCheckbox'
import {
  FrequencyEvolutionByLevelData,
  FrequencyEvolutionByLevelParams,
  FrequencyEvolutionByLevelResponse,
  getFrequencyEvolutionByLevel,
} from './api'

const lineColors = [
  'green', // nivel 1
  'emerald', // nivel 2
  'lime', // nivel 3
  'sky', // nivel 4
  'teal', // nivel 5
]

interface FrequencyEvolutionByLevelProps {
  // eslint-disable-next-line @typescript-eslint/member-delimiter-style
  period: { month: number; year: number }
  // eslint-disable-next-line react/require-default-props
  storeId?: number
  trigger: number
  // eslint-disable-next-line react/require-default-props
  onLoaded?: () => void
}

interface LevelInfo {
  name: string
  level: number
}

const downloadCSV = (data: FrequencyEvolutionByLevelData[], levels: LevelInfo[]) => {
  const headers = ['Mes', ...levels.map(level => level.name)]
  const csvRows = [
    headers,
    ...data.map(row => [row.month, ...levels.map(level => row[`nivel${level.level}`] || 0)]),
  ]
  const csvContent = csvRows.map(e => e.join(',')).join('\n')
  const blob = new Blob([csvContent], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)

  const a = document.createElement('a')
  a.href = url
  a.download = 'evolucion_frecuencia_niveles.csv'
  a.click()
  URL.revokeObjectURL(url)
}

const FrequencyEvolutionByLevel: React.FC<FrequencyEvolutionByLevelProps> = ({
  period,
  storeId = undefined,
  trigger,
  onLoaded,
}) => {
  const [showAllUsers, setShowAllUsers] = useState(false)
  const [showRecurrent, setShowRecurrent] = useState(true)
  const [data, setData] = useState<FrequencyEvolutionByLevelData[] | null>(null)
  const [levels, setLevels] = useState<LevelInfo[]>([])
  const canFetchRef = useRef(false)
  const skipFilterFetchRef = useRef(true)
  const [statsCall, statsLoading] = useApiCall<
    FrequencyEvolutionByLevelParams,
    FrequencyEvolutionByLevelResponse
  >(getFrequencyEvolutionByLevel)

  // Track loading state
  useLoadingTracker({ isLoading: statsLoading, componentId: 'FrequencyEvolutionByLevel' })

  const fetchData = async (callOnLoaded = false) => {
    setLevels([])
    const userType = showRecurrent ? 'recurrent' : 'all'
    try {
      const response = await statsCall({
        endDate: moment()
          .year(period.year)
          .month(period.month)
          .endOf('month')
          .format('YYYY-MM-DD'),
        storeId,
        userType,
      })
      if (response?.data) {
        const { dates, levels: levelsData } = response.data
        setLevels(levelsData)
        setData(dates)
      }
    } finally {
      if (callOnLoaded && onLoaded) {
        onLoaded()
      }
    }
  }

  useEffect(() => {
    if (trigger > 0) {
      canFetchRef.current = true
      fetchData(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger])

  useEffect(() => {
    if (skipFilterFetchRef.current) {
      skipFilterFetchRef.current = false
      return
    }
    if (!canFetchRef.current) return
    fetchData(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showRecurrent, showAllUsers])

  const handleAllUsersChange = (checked: boolean) => {
    if (checked) {
      setShowAllUsers(true)
      setShowRecurrent(false)
    } else {
      // Si intenta desmarcar, asegurar que el otro esté marcado
      if (!showRecurrent) {
        setShowRecurrent(true)
      }
      setShowAllUsers(false)
    }
  }

  const handleRecurrentChange = (checked: boolean) => {
    if (checked) {
      setShowRecurrent(true)
      setShowAllUsers(false)
    } else {
      // Si intenta desmarcar, asegurar que el otro esté marcado
      if (!showAllUsers) {
        setShowAllUsers(true)
      }
      setShowRecurrent(false)
    }
  }

  // Transform data for chart display
  const chartData =
    data?.map(item => {
      const chartItem: { [key: string]: string | number } = {
        month: moment(item.month, 'YYYY-MM').format('MMM YYYY'),
      }

      // Dynamically add level data based on available levels
      levels.forEach(level => {
        chartItem[`nivel${level.level}`] = item[`nivel${level.level}`] || 0
      })

      return chartItem
    }) || []

  if (statsLoading || !data) {
    return (
      <Card id="line-chart-niveles" className="w-full p-6 bg-white rounded-2xl shadow">
        <div className="flex flex-row justify-between items-center mb-2">
          <div>
            <Title
              className="text-base font-bold"
              style={{ fontFamily: 'Montserrat, sans-serif', color: '#1E1E1E' }}
            >
              Evolución de frecuencia por nivel
            </Title>
          </div>
        </div>
        <div className="flex items-center justify-center h-[300px]">
          {statsLoading ? (
            <CircularProgress size={40} />
          ) : (
            <Text>No hay datos disponibles para mostrar en este momento.</Text>
          )}
        </div>
      </Card>
    )
  }

  return (
    <Card id="line-chart-niveles" className="w-full p-6 bg-white rounded-2xl shadow">
      <div className="flex flex-row justify-between items-center mb-2">
        <div>
          <Title
            className="text-base font-bold"
            style={{ fontFamily: 'Montserrat, sans-serif', color: '#1E1E1E' }}
          >
            Evolución de frecuencia por nivel
          </Title>
          <div className="flex gap-4 mt-2">
            {levels.map((level, index) => {
              const colors = ['green', 'emerald', 'lime', 'sky', 'teal']
              const color = colors[index % colors.length]

              return (
                <div key={level.level} className="flex items-center gap-2">
                  <span className={`w-4 h-4 rounded bg-${color}-500`} />
                  <span className="text-xs font-semibold">{level.name}</span>
                </div>
              )
            })}
          </div>
        </div>
        <div className="flex gap-4 items-center">
          <label htmlFor="all-users" className="flex items-center gap-2 text-xs font-medium">
            <CustomCheckbox
              id="all-users"
              checked={showAllUsers}
              onChange={e => handleAllUsersChange(e.target.checked)}
              className="w-4 h-4 rounded border border-gray-400"
            />
            Todos los usuarios
          </label>
          <label
            htmlFor="recurrent-users-line"
            className="flex items-center gap-2 text-xs font-medium"
          >
            <CustomCheckbox
              id="recurrent-users-line"
              checked={showRecurrent}
              onChange={e => handleRecurrentChange(e.target.checked)}
              className="w-4 h-4 rounded border border-gray-400"
            />
            Usuarios recurrentes
          </label>
          <IconButton
            disabled={statsLoading}
            className="ml-2"
            aria-label="download"
            onClick={() => data && downloadCSV(data, levels)}
          >
            <GetAppIcon />
          </IconButton>
        </div>
      </div>
      <div className="w-full h-[300px] mb-6">
        <LineChart
          data={chartData}
          index="month"
          categories={levels.map(level => `nivel${level.level}`)}
          colors={lineColors}
          valueFormatter={v => `${v}`}
          yAxisWidth={30}
          showLegend={false}
          showAnimation={false}
        />
      </div>
    </Card>
  )
}

export default FrequencyEvolutionByLevel
